import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { EMPTY, Subscription, switchMap } from 'rxjs';
import { Envelope, Page, parseLoadKey, SourceRecord, text } from '../ufos/ufos-live.types';
import { UfosService } from '../ufos/ufos.service';

interface HistoryChange {
  label: string;
  before: string;
  after: string;
}

interface RecordedValue {
  label: string;
  value: string;
}

interface UfosHistoryEntry {
  recordedAt: string;
  author: string;
  type: string;
  notes: string;
  changes: HistoryChange[];
  recordedValues: RecordedValue[];
}

const HISTORY_FIELDS = [
  { key: 'status', label: 'Status' },
  { key: 'status2', label: 'Status detail' },
  { key: 'billto', label: 'Customer ID' },
  { key: 'carrier', label: 'Carrier ID' },
  { key: 'cost', label: 'Cost (source amount)' },
  { key: 'subtotal', label: 'Subtotal (source amount)' },
];

@Component({
  selector: 'app-ufos-history',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './ufos-history.component.html',
  styleUrls: ['./ufos-history.component.scss'],
})
export class UfosHistoryComponent implements OnChanges, OnDestroy {
  @Input() ladingUuid: string | null = null;
  @Input() active = false;

  entries: UfosHistoryEntry[] = [];
  workOrderNumber = '';
  loading = false;
  hasLoaded = false;
  error = '';
  coverage = '';
  retrievedAt = '';
  nextCursor: string | null = null;

  private loadNumber = '';
  private request?: Subscription;
  private requestSequence = 0;

  constructor(private readonly ufos: UfosService) {}

  ngOnChanges(changes: SimpleChanges): void {
    this.cancelPendingRequest();

    if (changes['ladingUuid']) {
      this.clearHistory();
    }
    if (this.active && this.ladingUuid) {
      this.refresh();
    }
  }

  ngOnDestroy(): void {
    this.cancelPendingRequest();
  }

  refresh(): void {
    this.cancelPendingRequest();
    this.clearHistory();

    if (!this.active || !this.ladingUuid) {
      return;
    }

    this.loading = true;
    const sequence = this.requestSequence;

    // Jenny: resolve the UFOs identity through the existing authorized viewer.
    // An AMS lading UUID is not itself a UFOs work-order number.
    this.request = this.ufos.read('resolve', { uuid: this.ladingUuid }).pipe(
      switchMap(response => {
        if (sequence !== this.requestSequence) {
          return EMPTY;
        }
        if (!response.ok || !response.data?.load_key) {
          throw new Error('UFOs could not resolve this work order. Refresh to try again.');
        }

        const identity = parseLoadKey(response.data.load_key);
        this.workOrderNumber = identity.wo;
        this.loadNumber = identity.load;

        return this.ufos.read('history', {
          wo_number: this.workOrderNumber,
          load_number: this.loadNumber,
        });
      })
    ).subscribe({
      next: response => {
        if (sequence === this.requestSequence) {
          this.acceptPage(response, false);
        }
      },
      error: error => this.showError(error, sequence),
    });
  }

  loadMore(): void {
    if (!this.active || this.loading || !this.nextCursor || !this.workOrderNumber) {
      return;
    }

    this.cancelPendingRequest();
    this.loading = true;
    this.error = '';
    const sequence = this.requestSequence;

    // Keep the server's opaque cursor and its oldest-to-newest ordering.
    this.request = this.ufos.read('history', {
      wo_number: this.workOrderNumber,
      load_number: this.loadNumber,
    }, this.nextCursor).subscribe({
      next: response => {
        if (sequence === this.requestSequence) {
          this.acceptPage(response, true);
        }
      },
      error: error => this.showError(error, sequence),
    });
  }

  private acceptPage(response: Envelope<Page>, append: boolean): void {
    if (!response.ok || !Array.isArray(response.data?.items) || !response.meta?.captured_at_utc) {
      this.error = 'UFOs returned an incomplete history response. Refresh to try again.';
      this.loading = false;
      return;
    }

    const page = response.data;
    const entries = page.items.map(row => this.describeEntry(row));
    this.entries = append ? [...this.entries, ...entries] : entries;
    this.nextCursor = page.has_more ? page.next_cursor : null;
    this.coverage = page.coverage;
    this.retrievedAt = response.meta.captured_at_utc;
    this.hasLoaded = true;
    this.loading = false;
  }

  private describeEntry(row: SourceRecord): UfosHistoryEntry {
    const values = row.fields;
    const recordedDate = text(values['date_changed']);
    const recordedTime = text(values['time_changed']);
    const changes: HistoryChange[] = [];
    const recordedValues: RecordedValue[] = [];

    for (const field of HISTORY_FIELDS) {
      const before = text(values['orig_' + field.key]);
      const after = text(values['new_' + field.key]);

      if (before !== after) {
        changes.push({ label: field.label, before, after });
      }
      if (before !== '') {
        recordedValues.push({ label: 'Previous ' + field.label, value: before });
      }
      if (after !== '') {
        recordedValues.push({ label: 'New ' + field.label, value: after });
      }
    }

    // Jenny: these are UFOs' original dates, author codes and source amounts.
    // Do not turn the author into a verified AMS user, use retrieval time as the
    // change date, or apply the AMS financial-history cents conversion here.
    return {
      recordedAt: [recordedDate, recordedTime].filter(Boolean).join(' · ') || 'Not recorded',
      author: text(values['user_id']) || 'Not recorded',
      type: text(values['type']) || 'Not recorded',
      notes: text(values['notes']),
      changes,
      recordedValues,
    };
  }

  private showError(error: unknown, sequence: number): void {
    // Late responses from a previous work order must not populate this tab.
    if (sequence !== this.requestSequence) {
      return;
    }

    if (error instanceof HttpErrorResponse) {
      switch (error.status) {
        case 404:
          this.error = 'No matching UFOs record was found for this work order.';
          break;
        case 401:
        case 403:
          this.error = 'Your session or account does not have access to UFOs history.';
          break;
        case 409:
          this.error = 'UFOs could not identify one unambiguous record. Have the source link checked.';
          break;
        default:
          this.error = 'UFOs history is unavailable. Refresh or retry loading more entries.';
      }
    } else {
      this.error = error instanceof Error ? error.message : 'UFOs history is unavailable.';
    }

    this.loading = false;
  }

  private clearHistory(): void {
    this.entries = [];
    this.workOrderNumber = '';
    this.loadNumber = '';
    this.nextCursor = null;
    this.coverage = '';
    this.retrievedAt = '';
    this.hasLoaded = false;
    this.error = '';
  }

  private cancelPendingRequest(): void {
    this.requestSequence++;
    this.request?.unsubscribe();
    this.loading = false;
  }
}
